/**
 * Pure shaping for the contact timeline: text cleanup, tier classification,
 * thread grouping and time formatting. No React, no imports — so `node --test`
 * can load it directly (test/timeline-model.test.js) and the browser bundle
 * gets the same code the tests cover.
 *
 * `.mjs` on purpose: the client is CRA (no "type": "module"), and the test
 * runner is CommonJS. An explicit ESM extension is unambiguous to both.
 */

// ---------------------------------------------------------------- text cleanup

// The named entities that actually turn up in Gmail snippets. Anything outside
// this list is left as written rather than guessed at — a stray "&foo;" in a
// body is the author's text, not an encoding artefact.
// `nbsp` becomes a PLAIN space, and the zero-width / soft-hyphen pair is dropped
// outright: these rows are dense one-liners, and an invisible character that
// blocks a wrap or splits a word is a layout bug you cannot see in the data.
const NAMED_ENTITIES = {
  amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " ",
  hellip: "…", mdash: "—", ndash: "–", laquo: "«", raquo: "»",
  lsquo: "‘", rsquo: "’", ldquo: "“", rdquo: "”", sbquo: "‚", bdquo: "„",
  middot: "·", bull: "•", deg: "°", euro: "€", pound: "£", yen: "¥",
  cent: "¢", copy: "©", reg: "®", trade: "™", eacute: "é", uuml: "ü",
  ouml: "ö", auml: "ä", ccedil: "ç", szlig: "ß", times: "×", minus: "−",
  zwnj: "", shy: "",
};

/**
 * HTML entities → characters. Gmail hands us snippets with `&#39;`, `&amp;` and
 * `&nbsp;` in them, so a stored body reads "Noa&#39;s team" until this runs.
 * Single pass: `&amp;amp;` decodes to `&amp;`, which is the correct answer for
 * text that was encoded twice on purpose.
 */
export function decodeEntities(text) {
  if (text === null || text === undefined) return "";
  return String(text).replace(/&(#[0-9]+|#[xX][0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]{1,31});/g, (whole, body) => {
    if (body[0] === "#") {
      const hex = body[1] === "x" || body[1] === "X";
      const code = parseInt(hex ? body.slice(2) : body.slice(1), hex ? 16 : 10);
      if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff) return whole;
      try {
        return String.fromCodePoint(code);
      } catch {
        return whole;
      }
    }
    const hit = NAMED_ENTITIES[body.toLowerCase()];
    return hit === undefined ? whole : hit;
  });
}

// Where a quoted reply chain begins. Gmail snippets arrive as ONE line with the
// newlines collapsed, so none of these can be anchored to a line start — the
// attribution line, the Outlook header block and the divider rules all have to
// match mid-string.
const QUOTE_STARTS = [
  // "On Fri, Aug 1, 2026 at 3:14 PM Gabriel Ehrlich <gabriel@remotion.io> wrote:"
  /\bOn\b[\s\S]{0,200}?\bwrote:/i,
  // Outlook / Apple Mail dividers.
  /-{2,}\s*Original Message\s*-{2,}/i,
  /-{2,}\s*Forwarded message\s*-{2,}/i,
  /_{10,}/,
  // Pasted header block: "From: … Sent: …" (or Date:, which Outlook web uses).
  /\bFrom:[\s\S]{0,200}?\b(?:Sent|Date):/i,
  // "Begin forwarded message:" (Apple Mail)
  /\bBegin forwarded message:/i,
];

/**
 * Everything from the start of the quoted chain onward, removed. Also drops
 * lines that are themselves quotes ("> …"), which is what survives when the
 * attribution line was truncated away before we ever saw it.
 *
 * Deliberately conservative: it cuts at the FIRST match, and if that leaves
 * nothing at all the original is returned — a body that is entirely one quote
 * is still more useful than a blank row.
 */
export function stripQuotedReply(text) {
  if (text === null || text === undefined) return "";
  const raw = String(text);
  let cut = raw.length;
  for (const pattern of QUOTE_STARTS) {
    const m = pattern.exec(raw);
    if (m && m.index < cut) cut = m.index;
  }
  let out = raw.slice(0, cut);
  // Quote markers at the head of a line, and the ">" runs a collapsed snippet
  // leaves behind between sentences.
  out = out
    .split("\n")
    .filter((line) => !/^\s*>/.test(line))
    .join("\n")
    .replace(/\s>+\s/g, " ")
    .trim();
  return out || raw.trim();
}

/**
 * A stored body as it should be read: entities decoded, quoted chain stripped,
 * runs of blank lines collapsed. Newlines survive — the expanded row shows the
 * body as written.
 */
export function cleanBody(text) {
  const decoded = decodeEntities(text);
  return stripQuotedReply(decoded).replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
}

/** The same text on one line, clipped — for the collapsed row summary. */
export function summarize(text, max = 120) {
  const s = cleanBody(text).replace(/\s+/g, " ").trim();
  if (!max || s.length <= max) return s;
  return `${s.slice(0, max).trimEnd()}…`;
}

// ------------------------------------------------------------------ tiering

export const TIER = { MILESTONE: "milestone", EVENT: "event", CHATTER: "chatter" };

/**
 * Three visual weights, per the brief:
 * - MILESTONE — stage and status changes (every stage_transition row: the
 *   contact's lifecycle_stage and lead_status history). A signed agreement
 *   reaches the record as one of these, not as an activity: PandaDoc proposes
 *   changes and never logs an activity of its own.
 * - CHATTER — email activities and web touchpoints: high volume, machine
 *   ingested, and the only tier that groups.
 * - EVENT — everything else at standard weight: calls, meetings, form
 *   submissions, notes, tasks and DMs. Notes and DMs are unlisted in the brief;
 *   they are authored by a person rather than swept up in bulk, so they take
 *   the middle tier rather than being faded into chatter.
 */
export function classifyTier(item) {
  if (!item) return TIER.EVENT;
  if (item.item_type === "stage_transition") return TIER.MILESTONE;
  if (item.item_type === "touchpoint") return TIER.CHATTER;
  if (item.subtype === "email") return TIER.CHATTER;
  return TIER.EVENT;
}

// ------------------------------------------------------------------ grouping

/** "Re: Fwd: RE: LinkedIn ads for DataOps" → "LinkedIn ads for DataOps". */
export function normalizeSubject(subject) {
  return String(subject || "")
    .replace(/^(\s*(?:re|fwd|fw|aw|sv)\s*(?:\[\d+\])?\s*:\s*)+/i, "")
    .replace(/\s+/g, " ")
    .trim();
}

// A thread key exists when Gmail gave us one (metadata.thread_id, captured by
// the watcher). Without it the normalized subject is the best available
// identity; with neither, the email never groups — a group we cannot justify is
// worse than a long list.
function threadKey(item) {
  if (!item || item.subtype !== "email") return null;
  const id = item.metadata && item.metadata.thread_id;
  if (id) return `t:${id}`;
  const subject = normalizeSubject(item.title);
  return subject ? `s:${subject.toLowerCase()}` : null;
}

const time = (ts) => {
  const n = new Date(ts).getTime();
  return Number.isFinite(n) ? n : 0;
};

/**
 * Consecutive same-thread email chatter → one group row. Order is preserved
 * (the timeline arrives newest-first) and only runs of 2+ collapse; a lone
 * email stays a plain row.
 *
 * Returns rows of either { kind: "item", key, item } or
 * { kind: "group", key, items, count, subject, from, to }, where from/to are
 * the oldest and newest timestamps in the run.
 */
export function groupThreads(items) {
  const rows = [];
  const list = Array.isArray(items) ? items : [];
  let run = [];
  let runKey = null;

  const flush = () => {
    if (!run.length) return;
    if (run.length === 1) {
      rows.push({ kind: "item", key: itemKey(run[0]), item: run[0] });
    } else {
      const stamps = run.map((it) => time(it.occurred_at));
      rows.push({
        kind: "group",
        key: `group-${runKey}-${itemKey(run[0])}`,
        items: run,
        count: run.length,
        subject: normalizeSubject(run[0].title),
        from: new Date(Math.min(...stamps)).toISOString(),
        to: new Date(Math.max(...stamps)).toISOString(),
      });
    }
    run = [];
    runKey = null;
  };

  for (const item of list) {
    const key = classifyTier(item) === TIER.CHATTER ? threadKey(item) : null;
    if (key && key === runKey) {
      run.push(item);
      continue;
    }
    flush();
    if (key) {
      run = [item];
      runKey = key;
    } else {
      rows.push({ kind: "item", key: itemKey(item), item });
    }
  }
  flush();
  return rows;
}

/** Stable per-row identity: the item type plus its own id. */
export function itemKey(item) {
  return `${item.item_type}-${item.id}`;
}

// ------------------------------------------------------------------ time/dates

const MD = { month: "short", day: "numeric" };

function fmtDate(ts, opts, timeZone) {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", timeZone ? { ...opts, timeZone } : opts);
}

function fmtTime(ts, timeZone) {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  const opts = { hour: "numeric", minute: "2-digit" };
  return d.toLocaleTimeString("en-US", timeZone ? { ...opts, timeZone } : opts);
}

/**
 * "Jul 31–Aug 3" for a group of emails; a single day when both ends land on it.
 * Years are appended only when the two ends disagree about the year, where
 * "Dec 30–Jan 2" would otherwise be a lie about a three-day span.
 */
export function dayRange(from, to, { timeZone } = {}) {
  const a = new Date(from);
  const b = new Date(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return "";
  const [lo, hi] = a <= b ? [a, b] : [b, a];
  const sameYear = fmtDate(lo, { year: "numeric" }, timeZone) === fmtDate(hi, { year: "numeric" }, timeZone);
  const opts = sameYear ? MD : { ...MD, year: "numeric" };
  const loText = fmtDate(lo, opts, timeZone);
  const hiText = fmtDate(hi, opts, timeZone);
  return loText === hiText ? loText : `${loText}–${hiText}`;
}

/**
 * A meeting's time range: "10:00–10:30 AM" when both ends share a meridiem,
 * "11:30 AM–1:00 PM" when they don't, the start alone when the calendar gave us
 * no end, and "all day" for a date-only event. En dash, no spaces around it.
 */
export function formatTimeRange(start, end, { allDay = false, timeZone } = {}) {
  if (allDay) return "all day";
  const startText = fmtTime(start, timeZone);
  if (!startText) return "";
  const endText = end ? fmtTime(end, timeZone) : "";
  if (!endText) return startText;
  if (endText === startText) return startText;
  const meridiem = (s) => (/\s(AM|PM)$/i.exec(s) || [])[1] || "";
  const bare = (s) => s.replace(/\s(AM|PM)$/i, "");
  const ma = meridiem(startText);
  const mb = meridiem(endText);
  if (ma && ma.toUpperCase() === mb.toUpperCase()) return `${bare(startText)}–${endText}`;
  return `${startText}–${endText}`;
}

/**
 * A meeting's range straight off the activity. `end_at` / `all_day` are written
 * by the calendar watcher; a meeting ingested before that capture existed has
 * neither, and shows its start time alone rather than an invented duration.
 */
export function meetingRange(item, { timeZone } = {}) {
  const meta = (item && item.metadata) || {};
  return formatTimeRange(item && item.occurred_at, meta.end_at || null, {
    allDay: !!meta.all_day,
    timeZone,
  });
}

/**
 * Rows split into day sections. The mockup replaces the per-row date with one
 * header per day and keeps only the clock time on the row — which is how a log
 * actually reads: you scan for the day, then for the moment inside it.
 *
 * Takes the output of groupThreads (so a thread group belongs to the day of its
 * newest message) and preserves order.
 */
export function byDay(rows, { timeZone } = {}) {
  const out = [];
  let current = null;
  const dayKey = (ts) => {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "unknown";
    return d.toLocaleDateString("en-CA", timeZone ? { timeZone } : undefined);
  };
  for (const row of rows || []) {
    const ts = row.kind === "group" ? row.to : row.item.occurred_at;
    const key = dayKey(ts);
    if (!current || current.key !== key) {
      current = { key, at: ts, rows: [] };
      out.push(current);
    }
    current.rows.push(row);
  }
  return out;
}

/** "AUG 5" — the day header. Uppercased by CSS, not here. */
export function dayHeading(ts, { timeZone } = {}) {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  const opts = { month: "short", day: "numeric" };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = "numeric";
  return d.toLocaleDateString("en-US", timeZone ? { ...opts, timeZone } : opts);
}

/** "14:05" — the row's own time, once the day is in the header. */
export function clockTime(ts, { timeZone } = {}) {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  const opts = { hour: "2-digit", minute: "2-digit", hour12: false };
  return d.toLocaleTimeString("en-GB", timeZone ? { ...opts, timeZone } : opts);
}

// --------------------------------------------------------------- signatures

/**
 * Where a trailing signature block begins. Deliberately conservative, and
 * deliberately FOLDING rather than deleting: detection that is wrong costs one
 * click, and deleting real content costs trust in everything else on the page.
 *
 * Run AFTER stripQuotedReply — a quoted chain contains signatures of its own, and
 * cutting at the first of those would take the reply with it.
 */
const SIGNATURE_STARTS = [
  // The RFC-ish delimiter: "--" or "__" alone on a line.
  /(^|\n)\s*(--|__)\s*(\n|$)/,
  /(^|\n)\s*-{3,}\s*(\n|$)/,
  // A long rule of underscores: Outlook opens its Teams invite block with one.
  /(^|\n)\s*_{10,}\s*(\n|$)/,
  // Mobile footers.
  /(^|\n)\s*Sent from my [\w\s]{1,20}(\n|$)/i,
  /(^|\n)\s*Get Outlook for \w+/i,
];

// A line that looks like signature furniture rather than prose: a phone number, a
// URL, an email address, a street address, or a title/company line with no verb.
const FURNITURE = [
  /^\s*(\+?\d[\d\s().-]{7,})\s*$/,
  /^\s*(https?:\/\/|www\.)\S+\s*$/i,
  /^\s*\S+@\S+\.\S+\s*$/,
  /^\s*\d+\s+[\w.'-]+(\s+[\w.'-]+){0,5},?\s+[A-Z]{2}\s+\d{5}/,
  /^\s*(m|t|p|o|d|c|tel|mob|cell|ph|fax)[:.]?\s*\+?\d[\d\s().-]{6,}\s*$/i,
  /^\s*(CEO|CTO|CMO|COO|CFO|VP|SVP|EVP|Founder|Director|Manager|Head of|President)\b[^.!?]{0,60}$/i,
];

// A short line that reads as a sign-off, which is where a signature usually starts.
const SIGNOFF = /^\s*(best|best regards|regards|kind regards|thanks|thank you|cheers|sincerely|warmly|all the best|talk soon|br)\b[,.!]?\s*$/i;

/**
 * Split a body into what was written and the signature trailing it.
 *
 * Returns { body, signature } — signature is null when nothing was detected, and
 * `body` is NEVER empty: if the whole message reads as furniture (a one-line reply
 * followed by a long block), the original is returned unfolded rather than leaving
 * a blank card.
 */
export function splitSignature(text) {
  const raw = String(text === null || text === undefined ? "" : text);
  if (!raw.trim()) return { body: "", signature: null };

  // 1. An explicit delimiter wins — it is the author telling us where it starts.
  let cut = raw.length;
  for (const pattern of SIGNATURE_STARTS) {
    const m = pattern.exec(raw);
    if (m && m.index < cut) cut = m.index;
  }

  // 2. Otherwise, walk back from the end over furniture lines to a sign-off.
  if (cut === raw.length) {
    const lines = raw.split("\n");
    let i = lines.length - 1;
    let furniture = 0;
    while (i >= 0) {
      const line = lines[i];
      if (!line.trim()) { i--; continue; }
      if (FURNITURE.some((re) => re.test(line))) { furniture++; i--; continue; }
      // A sign-off directly above furniture is the top of the block.
      if (furniture >= 2 && SIGNOFF.test(line)) { i--; break; }
      break;
    }
    // Two furniture lines is the floor: one stray URL at the end of a sentence is
    // not a signature.
    if (furniture >= 2) cut = lines.slice(0, i + 1).join("\n").length;
  }

  const body = raw.slice(0, cut).trim();
  const signature = raw.slice(cut).trim();
  if (!body || !signature) return { body: raw.trim(), signature: null };
  return { body, signature };
}

/** cleanBody, then folded: { body, signature }. */
export function cleanBodyFolded(text) {
  return splitSignature(cleanBody(text));
}

// ------------------------------------------------------------- email marks
//
// What an email IS, beyond who sent it: a row that carries our PRICING, and a
// row that is a NUDGE. Both are facts Gabriel reads off the thread by hand
// today — "which of these six messages had the numbers in it, and which ones
// were me chasing" — and both are derivable from what is already stored.
//
// Pure and thread-scoped on purpose: the timeline and the Latest-thread card
// each compute marks from what they hold (the whole timeline, one thread), and
// because nothing here looks outside the message's own thread the two can never
// disagree about a message they both show.

/**
 * Does this text quote a price — a money amount with a pricing term beside it?
 *
 * THE SAME SHAPE TEST automation rule 4 uses to mint the SQL (quotesPriceInline
 * in src/watchers/gmail.js), deliberately duplicated rather than approximated: a
 * message that moved a contact to SQL because it shared pricing, shown here with
 * no pricing chip, would be two answers to the one question. If that test
 * changes, this one changes with it.
 *
 * The money amount alone is not enough — outbound mail is full of the lead's own
 * ad spend ("you're at $40K/mo on LinkedIn"), which is a budget, not a quote,
 * and has no pricing term next to it.
 */
const MONEY_G = /[$€£₪]\s?\d[\d,.]*\s?[kKmM]?/g;
// "offer" rides in the list for the reason the header gives — it is rule 4's
// list, and DeepL's 2026-09-07 quote ("I think our offer will be pretty
// standard" over €5,000 + 10%) is the message that found the gap in both at
// once: no stage move, and a Pricing-sent card that said the terms were not on
// the timeline while the email carrying them sat two rows below it.
const PRICING_TERM = /\b(pricing|price|rates?|fees?|retainer|quoted?|charge|costs?|offer(?:s|ed|ing)?)\b/i;
const QUOTE_WINDOW = 160;

export function quotesPrice(text) {
  const s = String(text === null || text === undefined ? "" : text);
  for (const match of s.matchAll(MONEY_G)) {
    const from = Math.max(0, match.index - QUOTE_WINDOW);
    const to = match.index + match[0].length + QUOTE_WINDOW;
    if (PRICING_TERM.test(s.slice(from, to))) return true;
  }
  return false;
}

// A TERM LINE prices something and is short about it: "$5K for spend up to
// $25K", "+15% on any spend above that". Percentages count on their own — the
// second half of Gabriel's standard quote carries no currency symbol at all, and
// a block that stopped at the money lines would drop the half that scales. Prose
// about the deal ("We require a 3-month minimum commitment, after which one
// month's notice is required for cancellation") is neither short nor numeric,
// and ends the block.
const MONEY_LINE = /[$€£₪]\s?\d/;
const PERCENT_LINE = /\d+(?:\.\d+)?\s?%/;
const TERM_LINE_MAX = 90;

/*
 * GMAIL WRAPS AT ~75 COLUMNS, SO A QUOTE WRITTEN AS A SENTENCE ARRIVES IN PIECES.
 * Clover (2026-10-13): "On pricing, Tier 1 is $3,500 a month for up to $10,000
 * in monthly media" / "spend." / "Above that it's an additional 15% ... then
 * +10%" / "for another above $50K." The run kept the first fragment and lost the
 * rates. A long line that stops mid-sentence over a line that starts in
 * lowercase is one sentence: those rejoin, the paragraph splits back into
 * sentences, and each is a candidate term line up to WRAPPED_SENTENCE_MAX, since
 * a term written as prose runs longer than a listed one. A paragraph with a
 * longer sentence than that stays as it arrived. MIRRORED in
 * src/agreement/draft.js.
 */
const WRAP_MIN = 50;
const WRAPPED_SENTENCE_MAX = 120;

export function unwrapLines(lines) {
  const out = [];
  const wrapped = new Set();
  for (let i = 0; i < lines.length; i++) {
    const parts = [lines[i]];
    while (
      i + 1 < lines.length &&
      parts[parts.length - 1].length >= WRAP_MIN &&
      !/[.!?:;]$/.test(parts[parts.length - 1]) &&
      /^[a-z]/.test(lines[i + 1])
    ) {
      parts.push(lines[++i]);
    }
    const sentences = parts.length > 1 ? parts.join(" ").split(/(?<=[.!?])\s+(?=[A-Z$€£₪+])/) : parts;
    if (parts.length > 1 && sentences.every((s) => s.length <= WRAPPED_SENTENCE_MAX)) {
      for (const s of sentences) wrapped.add(s);
      out.push(...sentences);
    } else out.push(...parts);
  }
  return { lines: out, wrapped };
}

/*
 * WHICH RUN OF TERM LINES IS THE QUOTE. The first money line is not always the
 * price. Medison's follow-up (2026-10-05) recommended an ad budget first, "I'd
 * suggest a starting budget of $10K/m, but this can grow if we see", a short
 * hard-wrapped line, and only further down said "Our pricing:" over "$3,500 for
 * up to $10,000 in monthly budget." Taking the first run put their budget on the
 * card as what we quoted. So every run is a candidate, and the pick is the
 * first run that is about OUR price (a pricing word in it or in the line above
 * it: "Our pricing:", "we charge", "our fee is"), else the first run, as before.
 * Checked against all 229 quoting emails in production on 2026-10-06: a colon
 * rule instead picked discount notes and add-ons over the real terms.
 * MIRRORED in src/agreement/draft.js; test/agreement-draft.test.js holds the two
 * to the same answers. { start, terms } or null.
 */
export function termRun(lines, wrapped = new Set()) {
  const runs = [];
  for (let i = 0; i < lines.length; i++) {
    if (!MONEY_LINE.test(lines[i])) continue;
    const terms = [];
    let k = i;
    for (; k < lines.length; k++) {
      const line = lines[k];
      if (!line || line.length > (wrapped.has(line) ? WRAPPED_SENTENCE_MAX : TERM_LINE_MAX)) break;
      if (!MONEY_LINE.test(line) && !PERCENT_LINE.test(line)) break;
      terms.push(line);
    }
    if (terms.length) runs.push({ start: i, terms });
    if (k > i) i = k - 1;
  }
  if (!runs.length) return null;
  // About our price: a pricing word in the run itself or in the line above it.
  const priced = (r) => {
    let j = r.start - 1;
    while (j >= 0 && !lines[j]) j--;
    return PRICING_TERM.test([j >= 0 ? lines[j] : "", ...r.terms].join(" "));
  };
  return runs.find(priced) || runs[0];
}

/**
 * The terms out of a pricing email, VERBATIM: { intro, terms }, or null when the
 * body quotes nothing.
 *
 * Verbatim is the whole point. "$5K for spend up to $25K" plus "+15% on any
 * spend above that" is a STRUCTURE, not a number, and every attempt to reduce it
 * to one figure invents something — which is how "$25K/mo proposed" came to sit
 * on this card when $25K was the lead's own ad budget and our fee was $5K. These
 * lines are shown as they were sent, or not at all.
 */
export function pricingQuote(body) {
  const clean = cleanBody(body);
  if (!clean || !quotesPrice(clean)) return null;

  const { lines, wrapped } = unwrapLines(clean.split("\n").map((l) => l.trim()));
  const run = termRun(lines, wrapped);
  if (!run) return null;
  const { start, terms } = run;

  // The line that introduces them, when there is one: "To recap our pricing:".
  // Blank lines above are skipped; a line that is neither a pricing word nor a
  // colon is the sentence before, and belongs to the message, not to the quote.
  let j = start - 1;
  while (j >= 0 && !lines[j]) j--;
  const above = j >= 0 ? lines[j] : "";
  //
  // A HARD-WRAPPED SENTENCE IS NOT A LEAD-IN. Bodies arrive wrapped at ~72
  // columns, so the line above the terms is as often the tail of a paragraph as
  // an announcement of them: DeepL's is "complexity, I think our offer will be
  // pretty standard." — short enough, and carrying a pricing word, so it would
  // have printed above the terms as a fragment starting mid-sentence. An intro
  // either announces itself with a colon or at least begins a sentence, which
  // every intro this has ever found does ("To recap our pricing:", "Our pricing
  // is pretty simple:", "Regarding our pricing:").
  const announces = /:$/.test(above);
  const intro =
    above && above.length <= 120 && (announces || (/^[A-Z]/.test(above) && PRICING_TERM.test(above)))
      ? above
      : null;

  return { intro, terms };
}

// Direction, read the way describe() in Timeline.js reads it — and, since
// 2026-09-06, in the same THREE states it reads: theirs, ours, and not recorded.
// "Not inbound" used to stand in for "ours", which was false for every email the
// HubSpot import wrote (it never stored a direction at all), and the two readers
// below are the ones where that mattered: an unknown email could be marked as our
// own pricing quote or our own nudge. Both now ask for outbound explicitly.
const isInbound = (it) => ((it && it.metadata) || {}).direction === "inbound";
const isOutbound = (it) => ((it && it.metadata) || {}).direction === "outbound";

/**
 * An outbound email that quotes our pricing. Inbound is excluded by DIRECTION,
 * not by wording: the lead naming their own budget is the most common money
 * sentence in the mailbox, and it is not our quote.
 *
 * Read from the BODY alone. The subject line is where rule 4 looks for its
 * attachment-shaped proposals; the question here is what the message actually
 * says, and a subject that mentions pricing over a body that does not is not a
 * message you can read terms off.
 */
export function isPricingEmail(item) {
  if (!item || item.subtype !== "email" || !isOutbound(item)) return false;
  if ((item.metadata || {}).bulk) return false;
  return !!pricingQuote(item.body);
}

/**
 * WHAT IS ON OFFER NOW (migration 065).
 *
 * The FIRST pricing email's terms, verbatim — unless Gabriel has approved newer
 * ones from a later email (his revision, or their counter: Sygnia's "the 13%
 * model with a $5,500 monthly minimum is better for us"), which supersede it.
 * A later email that has not been approved does NOT move the quote: it waits in
 * Approvals as a set_quote, and `pendingQuote` below says so beside it.
 *
 *   { intro, terms, sent_at, revised_at, revised_by, first_email, item, thread_id } | null
 *
 * sent_at is the first pricing email (when pricing went out); revised_at the
 * approved email's own date, never the approval's.
 */
export function quoteOnRecord(rows, company) {
  const mail = (rows || []).filter((r) => isPricingEmail(r)).sort((a, b) => time(a.occurred_at) - time(b.occurred_at));
  const first = mail[0] || null;
  const co = company || {};
  const approved = String(co.quoted_terms || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  const at = co.quoted_terms_at || null;
  if (approved.length && time(at) && (!first || time(at) >= time(first.occurred_at))) {
    const item = (rows || []).find((r) => r.subtype === "email" && time(r.occurred_at) === time(at)) || first;
    return {
      intro: null,
      terms: approved,
      sent_at: first ? first.occurred_at : at,
      revised_at: first ? at : null,
      revised_by: co.quoted_terms_by || null,
      first_email: !!first,
      item,
      thread_id: item ? (item.metadata || {}).thread_id || null : null,
    };
  }
  const q = first ? pricingQuote(first.body) : null;
  if (!q) return null;
  return {
    intro: q.intro,
    terms: q.terms,
    sent_at: first.occurred_at,
    revised_at: null,
    revised_by: null,
    first_email: true,
    item: first,
    thread_id: (first.metadata || {}).thread_id || null,
  };
}

/**
 * "Pricing email, Oct 8 · revised Oct 13 in their reply": where the quote on
 * record came from, in the card's words. `d` formats a date.
 */
export function quoteSourceWords(quote, d) {
  if (!quote) return "";
  if (!quote.first_email) return `${quote.revised_by === "them" ? "Their" : "Our"} email, ${d(quote.sent_at)}`;
  const revised = quote.revised_at ? `revised ${d(quote.revised_at)}${quote.revised_by === "them" ? " in their reply" : ""}` : null;
  return [`Pricing email, ${d(quote.sent_at)}`, revised].filter(Boolean).join(" · ");
}

/**
 * The newest pricing still waiting in Approvals for this record (a set_quote
 * from act.pending), or null: { id, terms, by, at, evidence }.
 */
export function pendingQuote(pending) {
  const waiting = (pending || []).filter((p) => p && p.change_type === "set_quote" && p.payload && Array.isArray(p.payload.terms));
  if (!waiting.length) return null;
  const p = waiting.reduce((a, b) => (time(b.payload.effective_date) > time(a.payload.effective_date) ? b : a));
  return { id: p.id, terms: p.payload.terms, by: p.payload.by || null, at: p.payload.effective_date || null, evidence: p.evidence || null };
}

// A second message inside the hour is Gabriel remembering something ("forgot to
// mention — the deck is attached"), not a chase. The cadence calls a nudge due
// after 4 quiet days (cadence_nudge_days, src/actions/model.js); a day is the
// floor below which "following up" cannot be what happened.
const NUDGE_MIN_GAP_MS = 24 * 60 * 60 * 1000;

/**
 * A NUDGE: our message following our own last message on the same thread, with
 * nothing from them in between and at least a day's gap.
 *
 * `prev` is the message immediately before this one ON ITS THREAD — so "no reply
 * in between" needs no search: had they replied, the reply would be `prev`. The
 * first message on a thread opens it, and is never a nudge.
 */
export function isNudge(item, prev) {
  if (!item || item.subtype !== "email" || !isOutbound(item)) return false;
  if (!prev || !isOutbound(prev)) return false;
  return time(item.occurred_at) - time(prev.occurred_at) >= NUDGE_MIN_GAP_MS;
}

/** The marks themselves — the label, and the sentence the chip explains itself with. */
export const MARKS = {
  pricing: { key: "pricing", label: "pricing", title: "This message quotes our pricing" },
  nudge: { key: "nudge", label: "nudge", title: "A follow-up on our own last message, with no reply in between" },
};

/**
 * Every email in `items` → its marks, keyed by itemKey. Order-independent: the
 * list is sorted oldest-first internally, so the timeline (newest-first) and the
 * thread card (oldest-first) get the same answer for the same message.
 */
export function emailMarks(items) {
  const out = new Map();
  const emails = (Array.isArray(items) ? items : [])
    .filter((it) => it && it.subtype === "email" && !(it.metadata || {}).bulk)
    .slice()
    .sort((a, b) => time(a.occurred_at) - time(b.occurred_at));

  const previous = new Map();
  for (const it of emails) {
    const key = threadKey(it) || "t:ungrouped";
    const prev = previous.get(key) || null;
    previous.set(key, it);

    const marks = [];
    if (isPricingEmail(it)) marks.push(MARKS.pricing);
    if (isNudge(it, prev)) marks.push(MARKS.nudge);
    if (marks.length) out.set(itemKey(it), marks);
  }
  return out;
}

// ------------------------------------------------------------- gmail links

/**
 * The Gmail web URL for a thread.
 *
 * ONE vocabulary with the server's `threadUrl` (src/actions/reply.js), which
 * builds the same URL for the reply flow's "open the thread" link. Two formats
 * for the same destination is two things to get wrong.
 *
 * `/u/0/` is the first signed-in Google account, which is what the server has
 * always used; on a browser signed into several accounts Gmail resolves the
 * thread against that one.
 */
export function gmailThreadUrl(threadId) {
  const id = typeof threadId === "string" ? threadId.trim() : "";
  return id ? `https://mail.google.com/mail/u/0/#all/${encodeURIComponent(id)}` : null;
}

/**
 * The thread id an item belongs to, or null. Only Gmail-ingested email has one
 * — a thread grouped by subject alone (see threadKey) has nothing to open, and
 * a link built from a subject would 404 in the least useful way.
 */
export function threadIdOf(item) {
  const id = item && item.metadata && item.metadata.thread_id;
  return typeof id === "string" && id.trim() ? id.trim() : null;
}

/**
 * WHAT IS OWED ON A THREAD: a reply, or a chase.
 *
 * The thread card's button used to say "Reply" whatever the conversation looked
 * like, and drafted the reply_waiting kind - whose whole instruction to the model
 * is "answer the actual question they asked". On a thread where every message is
 * ours there is no question, so it wrote a reply to our own last email.
 *
 * Decided by WHO SPOKE LAST, not by whether an inbound exists anywhere in the
 * thread: a conversation they opened and we answered contains an inbound and owes
 * nothing. The kinds are the drafter's own (src/email/index.js), so the button's
 * word and the prompt behind it cannot drift apart.
 */
export function threadOwes(messages) {
  const list = messages || [];
  const last = list[list.length - 1];
  const inbound = !!(last && last.metadata && last.metadata.direction === "inbound");
  return inbound
    ? { verb: "Reply", kind: "reply_waiting" }
    : { verb: "Nudge", kind: "nudge_due" };
}

// FIREFLIES WRITES MARKDOWN, AND THE PANEL RENDERS TEXT. The summary arrives as
// "- **Business Model:** Paid emissions measurement…" lines and the action items
// as one string grouped under "**Ecologi team**" / "**Gabriel Ehrlich**" headers,
// with a "(08:45)" timestamp on each. Printed raw, the Last call panel showed the
// asterisks, and the action items did not show at all: the panel only listed an
// ARRAY, and the watcher stores this string (Ecologi, 2026-09-29).

const BOLD_LINE = /^\*\*(.+?)\*\*:?$/;

/** Summary bullets: [{ label, text }]; label is null for a line with no bold lead. */
export function callSummaryPoints(body) {
  if (!body) return [];
  return String(body)
    .split("\n")
    .map((line) => line.trim().replace(/^[-*•]\s+/, ""))
    .filter(Boolean)
    .map((line) => {
      const lead = line.match(/^\*\*(.+?):?\*\*:?\s*(.*)$/);
      if (lead) return { label: lead[1].replace(/:$/, ""), text: lead[2].replace(/\*\*/g, "") };
      return { label: null, text: line.replace(/\*\*/g, "") };
    });
}

/**
 * Action items grouped by owner: [{ owner, items }]. Accepts the stored string
 * or an array. owner is null for items before any header (or with no headers).
 */
export function callActionItems(raw) {
  const lines = Array.isArray(raw) ? raw : raw ? String(raw).split("\n") : [];
  const groups = [];
  let current = null;
  for (const rawLine of lines) {
    const line = String(rawLine).trim().replace(/^[-*•]\s+/, "");
    if (!line) continue;
    const header = line.match(BOLD_LINE);
    if (header) {
      current = { owner: header[1], items: [] };
      groups.push(current);
      continue;
    }
    if (!current) {
      current = { owner: null, items: [] };
      groups.push(current);
    }
    current.items.push(line.replace(/\*\*/g, "").replace(/\s*\(\d{1,2}:\d{2}(?::\d{2})?\)$/, ""));
  }
  return groups.filter((g) => g.items.length);
}
